import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { format } from 'date-fns';
import { SessionState, MaterializedView, Email, SendEmailRequest } from '@/types';
import { api } from '@/services/api';
import { useUIStore } from '@/stores';
import ContactAutocomplete, { Contact } from './ContactAutocomplete';
import {
  Mail,
  Send,
  X,
  Reply,
  Star,
  Archive,
  Edit3,
  Search,
  AlertCircle,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';

interface EmailInterfaceProps {
  session: SessionState;
  materializedView: MaterializedView;
  initialComposeTo?: string;
}

type FolderType = 'inbox' | 'sent' | 'archive';

const EmailInterface: React.FC<EmailInterfaceProps> = ({
  session,
  materializedView,
  initialComposeTo,
}) => {
  const { addNotification } = useUIStore();
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composeData, setComposeData] = useState<SendEmailRequest>({
    to: '',
    subject: '',
    body: '',
  });
  const [sending, setSending] = useState(false);
  const [folder, setFolder] = useState<FolderType>('inbox');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedThreads, setExpandedThreads] = useState<Set<string>>(new Set());

  // Open composer with pre-populated To when launched from Contact card
  useEffect(() => {
    if (initialComposeTo) {
      setComposeData({ to: initialComposeTo, subject: '', body: '' });
      setComposerOpen(true);
    }
  }, [initialComposeTo]);

  // Hardcoded contacts for MVP
  const contacts: Contact[] = useMemo(() => [
    { id: 'jennifer-martinez', name: 'Jennifer Martinez', email: 'jennifer.martinez@company.com', role: 'Chief Executive Officer', aiEnabled: true },
    { id: 'marcus-johnson', name: 'Marcus Johnson', email: 'marcus.johnson@company.com', role: 'Senior Account Manager', aiEnabled: true },
    { id: 'sarah-chen', name: 'Sarah Chen', email: 'sarah.chen@company.com', role: 'QA Team Lead', aiEnabled: true },
    { id: 'tom-bradley', name: 'Tom Bradley', email: 'tom.bradley@company.com', role: 'Team Member', aiEnabled: true },
    { id: 'lisa-wang', name: 'Lisa Wang', email: 'lisa.wang@company.com', role: 'Team Member', aiEnabled: true },
    { id: 'james-foster', name: 'James Foster', email: 'james.foster@company.com', role: 'Team Member', aiEnabled: true },
    { id: 'maria-rodriguez', name: 'Maria Rodriguez', email: 'maria.rodriguez@company.com', role: 'Team Member', aiEnabled: true },
    { id: 'kevin-patel', name: 'Kevin Patel', email: 'kevin.patel@company.com', role: 'Team Member', aiEnabled: true },
    { id: 'product-team', name: 'Product Team', email: 'product-team@company.com', role: 'Department', aiEnabled: false },
    { id: 'finance-team', name: 'Finance Team', email: 'finance-team@company.com', role: 'Department', aiEnabled: false },
    { id: 'hr-department', name: 'HR Department', email: 'hr@company.com', role: 'Department', aiEnabled: false },
    { id: 'it-helpdesk', name: 'IT Helpdesk', email: 'helpdesk@company.com', role: 'Support', aiEnabled: false },
    { id: 'exec-assistant', name: 'Executive Assistant', email: 'exec-assistant@company.com', role: 'Administrative', aiEnabled: false },
    { id: 'compliance-team', name: 'Compliance Team', email: 'compliance@company.com', role: 'Department', aiEnabled: false },
    { id: 'marketing-team', name: 'Marketing Team', email: 'marketing@company.com', role: 'Department', aiEnabled: false },
    { id: 'security-team', name: 'Security Team', email: 'security@company.com', role: 'Department', aiEnabled: false },
    { id: 'platform-team-lead', name: 'Platform Team Lead', email: 'platform-lead@company.com', role: 'Engineering', aiEnabled: false },
    { id: 'design-team', name: 'Design Team', email: 'design@company.com', role: 'Department', aiEnabled: false },
    { id: 'legal-team', name: 'Legal Team', email: 'legal@company.com', role: 'Department', aiEnabled: false },
    { id: 'sales-ops', name: 'Sales Operations', email: 'sales-ops@company.com', role: 'Department', aiEnabled: false },
    { id: 'customer-success', name: 'Customer Success', email: 'customersuccess@company.com', role: 'Department', aiEnabled: false },
    { id: 'data-team', name: 'Data Team', email: 'data@company.com', role: 'Department', aiEnabled: false },
    { id: 'facilities', name: 'Facilities', email: 'facilities@company.com', role: 'Operations', aiEnabled: false },
    { id: 'recruiting', name: 'Recruiting', email: 'recruiting@company.com', role: 'HR', aiEnabled: false },
    { id: 'expense-system', name: 'Expense System', email: 'expenses@company.com', role: 'System', aiEnabled: false },
    { id: 'benefits-admin', name: 'Benefits Admin', email: 'benefits@company.com', role: 'HR', aiEnabled: false },
    { id: 'company-events', name: 'Company Events', email: 'events@company.com', role: 'Social', aiEnabled: false },
    { id: 'newsletter', name: 'Company Newsletter', email: 'newsletter@company.com', role: 'Communications', aiEnabled: false },
    { id: 'office-manager', name: 'Office Manager', email: 'office@company.com', role: 'Operations', aiEnabled: false },
    { id: 'wellness-program', name: 'Wellness Program', email: 'wellness@company.com', role: 'HR', aiEnabled: false },
    { id: 'training-team', name: 'Training Team', email: 'training@company.com', role: 'L&D', aiEnabled: false },
    { id: 'internal-comms', name: 'Internal Communications', email: 'comms@company.com', role: 'Communications', aiEnabled: false },
    { id: 'dev-tools-team', name: 'Dev Tools Team', email: 'devtools@company.com', role: 'Engineering', aiEnabled: false },
    { id: 'diversity-committee', name: 'Diversity Committee', email: 'diversity@company.com', role: 'Committee', aiEnabled: false },
    { id: 'board-liaison', name: 'Board Liaison', email: 'board@company.com', role: 'Executive', aiEnabled: false },
    { id: 'patent-attorney', name: 'Patent Attorney', email: 'patents@company.com', role: 'Legal', aiEnabled: false },
    { id: 'customer-advisory', name: 'Customer Advisory Board', email: 'cab@company.com', role: 'Customer Relations', aiEnabled: false },
    { id: 'investor-relations', name: 'Investor Relations', email: 'ir@company.com', role: 'Finance', aiEnabled: false },
    { id: 'sustainability-committee', name: 'Sustainability Committee', email: 'sustainability@company.com', role: 'Committee', aiEnabled: false },
    { id: 'linkedin-noreply', name: 'LinkedIn', email: 'noreply@linkedin.com', role: 'External', aiEnabled: false },
    { id: 'amazon-orders', name: 'Amazon Orders', email: 'orders@amazon.com', role: 'External', aiEnabled: false },
    { id: 'github-notifications', name: 'GitHub', email: 'notifications@github.com', role: 'External', aiEnabled: false },
    { id: 'conference-invite', name: 'Conference Invites', email: 'conferences@events.com', role: 'External', aiEnabled: false },
    { id: 'slack-digest', name: 'Slack Digest', email: 'digest@slack.com', role: 'External', aiEnabled: false },
    { id: 'competitor-alerts', name: 'Competitor Alerts', email: 'alerts@competitorwatch.com', role: 'External', aiEnabled: false },
    { id: 'zoom-recordings', name: 'Zoom Recordings', email: 'recordings@zoom.us', role: 'External', aiEnabled: false },
    { id: 'coffee-chat-bot', name: 'Coffee Chat Bot', email: 'coffeebot@company.com', role: 'Bot', aiEnabled: false },
  ].sort((a, b) => {
    if (a.aiEnabled !== b.aiEnabled) return a.aiEnabled ? -1 : 1;
    return a.name.localeCompare(b.name);
  }), []);

  // Helper to resolve sender ID to email address
  const getEmailAddress = useCallback((senderId: string): string => {
    const contact = contacts.find(c => c.id === senderId);
    return contact?.email || senderId;
  }, [contacts]);

  // Helper to get sender display name
  const getSenderName = useCallback((senderId: string): string => {
    const contact = contacts.find(c => c.id === senderId);
    return contact?.name || senderId;
  }, [contacts]);

  // Get sender initials for avatar
  const getSenderInitials = useCallback((senderId: string): string => {
    const name = getSenderName(senderId);
    const parts = name.split(' ');
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.substring(0, 2).toUpperCase();
  }, [getSenderName]);

  // Generate a consistent color from sender ID
  const getSenderColor = useCallback((senderId: string): string => {
    const colors = [
      'bg-blue-600', 'bg-purple-600', 'bg-pink-600', 'bg-red-600',
      'bg-orange-600', 'bg-amber-600', 'bg-teal-600', 'bg-cyan-600',
    ];
    let hash = 0;
    for (let i = 0; i < senderId.length; i++) {
      hash = senderId.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  }, []);

  // Build thread map: group emails by subject root
  const getThreadKey = useCallback((email: Email): string => {
    const subject = email.subject.replace(/^(RE:\s*)+/i, '').trim();
    return subject.toLowerCase();
  }, []);

  // Filter and sort emails based on folder, search, and archive status
  const filteredEmails = useMemo(() => {
    let emails = materializedView.inbox.emails;

    // Filter by folder
    switch (folder) {
      case 'inbox':
        emails = emails.filter(e => e.to === 'player' && !e.isArchived);
        break;
      case 'sent':
        emails = emails.filter(e => e.from === 'player');
        break;
      case 'archive':
        emails = emails.filter(e => e.isArchived);
        break;
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      emails = emails.filter(e =>
        e.subject.toLowerCase().includes(query) ||
        getSenderName(e.from).toLowerCase().includes(query) ||
        getSenderName(e.to).toLowerCase().includes(query) ||
        e.body.toLowerCase().includes(query)
      );
    }

    // Sort by timestamp (newest first)
    return [...emails].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [materializedView.inbox.emails, folder, searchQuery, getSenderName]);

  // Group emails into threads for the reading pane
  const getThreadEmails = useCallback((email: Email): Email[] => {
    const threadKey = getThreadKey(email);
    const allEmails = materializedView.inbox.emails;

    const threadEmails = allEmails
      .filter(e => getThreadKey(e) === threadKey)
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    return threadEmails.length > 1 ? threadEmails : [];
  }, [materializedView.inbox.emails, getThreadKey]);

  // Folder counts
  const inboxCount = useMemo(() =>
    materializedView.inbox.emails.filter(e => e.to === 'player' && !e.isArchived && !e.isRead).length,
    [materializedView.inbox.emails]
  );

  const sentCount = useMemo(() =>
    materializedView.inbox.emails.filter(e => e.from === 'player').length,
    [materializedView.inbox.emails]
  );

  const archiveCount = useMemo(() =>
    materializedView.inbox.emails.filter(e => e.isArchived).length,
    [materializedView.inbox.emails]
  );

  // Mark email as read when selected
  const handleSelectEmail = useCallback(async (email: Email) => {
    setSelectedEmail(email);

    if (!email.isRead && email.to === 'player') {
      try {
        await api.markEmailRead(session.id, email.id);
      } catch (error) {
        console.error('Error marking email as read:', error);
      }
    }
  }, [session.id]);

  // Archive email
  const handleArchiveEmail = useCallback(async (email: Email) => {
    try {
      await api.archiveEmail(session.id, email.id);
      addNotification({ type: 'success', message: 'Email archived' });
      if (selectedEmail?.id === email.id) {
        setSelectedEmail(null);
      }
    } catch (error) {
      console.error('Error archiving email:', error);
      addNotification({ type: 'error', message: 'Failed to archive email' });
    }
  }, [session.id, selectedEmail, addNotification]);

  // Flag/unflag email
  const handleFlagEmail = useCallback(async (email: Email) => {
    try {
      await api.flagEmail(session.id, email.id);
      addNotification({
        type: 'success',
        message: email.isFlagged ? 'Flag removed' : 'Email flagged',
      });
    } catch (error) {
      console.error('Error flagging email:', error);
      addNotification({ type: 'error', message: 'Failed to flag email' });
    }
  }, [session.id, addNotification]);

  const handleComposeEmail = useCallback((replyTo?: Email) => {
    if (replyTo) {
      setComposeData({
        to: getEmailAddress(replyTo.from),
        subject: replyTo.subject.startsWith('RE:') ? replyTo.subject : `RE: ${replyTo.subject}`,
        body: `\n\n--- Original Message ---\nFrom: ${getSenderName(replyTo.from)}\nDate: ${format(new Date(replyTo.timestamp), 'PPp')}\nSubject: ${replyTo.subject}\n\n${replyTo.body}`,
      });
    } else {
      setComposeData({ to: '', subject: '', body: '' });
    }
    setComposerOpen(true);
  }, [getEmailAddress, getSenderName]);

  const handleSendEmail = async () => {
    if (!composeData.to || !composeData.body) {
      addNotification({ type: 'error', message: 'Please fill in recipient and message' });
      return;
    }

    try {
      setSending(true);
      await api.sendEmail(session.id, composeData);
      addNotification({ type: 'success', message: 'Email sent successfully' });
      setComposerOpen(false);
      setComposeData({ to: '', subject: '', body: '' });
    } catch (error) {
      console.error('Error sending email:', error);
      addNotification({ type: 'error', message: 'Failed to send email' });
    } finally {
      setSending(false);
    }
  };

  const toggleThread = useCallback((threadKey: string) => {
    setExpandedThreads(prev => {
      const next = new Set(prev);
      if (next.has(threadKey)) {
        next.delete(threadKey);
      } else {
        next.add(threadKey);
      }
      return next;
    });
  }, []);

  return (
    <div className="flex h-full bg-slate-800 text-white font-sans">
      {/* Email List Sidebar */}
      <div className="w-96 bg-slate-900 border-r border-slate-600 flex flex-col">
        {/* Sidebar Header */}
        <div className="p-4 border-b border-slate-600">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-400" />
              <h2 className="text-lg font-bold text-white capitalize">{folder}</h2>
            </div>
            <button
              onClick={() => handleComposeEmail()}
              className="flex items-center gap-2 bg-blue-500 hover:bg-blue-500/90 text-white px-4 py-2 rounded-lg font-bold text-sm transition-colors"
            >
              <Edit3 className="w-4 h-4" />
              Compose
            </button>
          </div>

          {/* Search Bar */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search emails..."
              className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                <X className="w-3.5 h-3.5 text-slate-400 hover:text-white" />
              </button>
            )}
          </div>

          {/* Folder Navigation Tabs */}
          <div className="flex bg-slate-800 rounded-lg p-1 border border-slate-600">
            <button
              onClick={() => { setFolder('inbox'); setSelectedEmail(null); }}
              className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all relative ${folder === 'inbox'
                ? 'bg-slate-700 text-blue-400 shadow-sm'
                : 'text-slate-400 hover:text-slate-300'
              }`}
            >
              Inbox
              {inboxCount > 0 && (
                <span className="ml-1 text-[10px] bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded-full">
                  {inboxCount}
                </span>
              )}
            </button>
            <button
              onClick={() => { setFolder('sent'); setSelectedEmail(null); }}
              className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${folder === 'sent'
                ? 'bg-slate-700 text-blue-400 shadow-sm'
                : 'text-slate-400 hover:text-slate-300'
              }`}
            >
              Sent
              {sentCount > 0 && (
                <span className="ml-1 text-[10px] text-slate-500">{sentCount}</span>
              )}
            </button>
            <button
              onClick={() => { setFolder('archive'); setSelectedEmail(null); }}
              className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${folder === 'archive'
                ? 'bg-slate-700 text-blue-400 shadow-sm'
                : 'text-slate-400 hover:text-slate-300'
              }`}
            >
              Archive
              {archiveCount > 0 && (
                <span className="ml-1 text-[10px] text-slate-500">{archiveCount}</span>
              )}
            </button>
          </div>
        </div>

        {/* Email List */}
        <div className="flex-1 overflow-y-auto">
          {filteredEmails.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-8">
              <Mail className="w-12 h-12 text-slate-600 mb-4" />
              <p className="text-slate-300 text-sm">
                {searchQuery ? 'No matching emails' : 'No emails yet'}
              </p>
            </div>
          ) : (
            filteredEmails.map((email) => {
              const isSelected = selectedEmail?.id === email.id;
              const displayName = folder === 'sent'
                ? `To: ${getSenderName(email.to)}`
                : getSenderName(email.from);
              const isUnread = !email.isRead && folder !== 'sent';
              const senderId = folder === 'sent' ? email.to : email.from;

              return (
                <button
                  key={email.id}
                  onClick={() => handleSelectEmail(email)}
                  className={`w-full p-3 border-b border-slate-600 text-left transition-colors ${
                    isSelected
                      ? 'bg-slate-900 border-l-4 border-l-blue-400'
                      : 'hover:bg-slate-900/50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Avatar */}
                    <div className={`w-8 h-8 rounded-full ${getSenderColor(senderId)} flex items-center justify-center flex-shrink-0 text-xs font-bold text-white`}>
                      {getSenderInitials(senderId)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className={`text-sm truncate ${isUnread ? 'font-bold text-white' : 'font-normal text-slate-200'}`}>
                          {displayName}
                        </span>
                        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                          {email.importance === 'high' && (
                            <AlertCircle className="w-3.5 h-3.5 text-red-500" />
                          )}
                          {email.isFlagged && (
                            <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                          )}
                          {isUnread && (
                            <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                          )}
                        </div>
                      </div>
                      <div className={`text-sm mb-0.5 ${isUnread ? 'font-semibold' : 'font-normal'} text-white line-clamp-1`}>
                        {email.subject}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-400 line-clamp-1">
                          {email.body.substring(0, 60)}...
                        </span>
                        <span className="text-xs text-slate-400 flex-shrink-0 ml-2">
                          {format(new Date(email.timestamp), 'h:mm a')}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Email View Pane */}
      <div className="flex-1 flex flex-col bg-slate-800">
        {selectedEmail ? (
          <>
            {/* Email Header */}
            <div className="p-6 pr-20 border-b border-slate-600">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {selectedEmail.importance === 'high' && (
                      <span className="text-xs font-bold bg-red-500/20 text-red-400 px-2 py-0.5 rounded">
                        HIGH PRIORITY
                      </span>
                    )}
                    <h1 className="text-xl font-bold text-white">
                      {selectedEmail.subject}
                    </h1>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full ${getSenderColor(selectedEmail.from)} flex items-center justify-center text-xs font-bold text-white`}>
                      {getSenderInitials(selectedEmail.from)}
                    </div>
                    <div>
                      <div className="text-sm text-white font-medium">
                        {getSenderName(selectedEmail.from)}
                      </div>
                      <div className="text-xs text-slate-400">
                        To: {getSenderName(selectedEmail.to)}
                        <span className="mx-2">·</span>
                        {format(new Date(selectedEmail.timestamp), 'PPp')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-1">
                  <button
                    onClick={() => handleComposeEmail(selectedEmail)}
                    className="p-2 hover:bg-slate-900 rounded-lg transition-colors group"
                    title="Reply"
                  >
                    <Reply className="w-5 h-5 text-slate-300 group-hover:text-blue-400" />
                  </button>
                  <button
                    onClick={() => handleFlagEmail(selectedEmail)}
                    className="p-2 hover:bg-slate-900 rounded-lg transition-colors group"
                    title={selectedEmail.isFlagged ? 'Remove flag' : 'Flag'}
                  >
                    <Star className={`w-5 h-5 ${selectedEmail.isFlagged ? 'text-amber-500 fill-amber-500' : 'text-slate-300 group-hover:text-amber-500'}`} />
                  </button>
                  <button
                    onClick={() => handleArchiveEmail(selectedEmail)}
                    className="p-2 hover:bg-slate-900 rounded-lg transition-colors group"
                    title="Archive"
                  >
                    <Archive className="w-5 h-5 text-slate-300 group-hover:text-blue-400" />
                  </button>
                </div>
              </div>
            </div>

            {/* Thread History (if conversation exists) */}
            {(() => {
              const threadEmails = getThreadEmails(selectedEmail);
              if (threadEmails.length === 0) return null;

              const threadKey = getThreadKey(selectedEmail);
              const isExpanded = expandedThreads.has(threadKey);
              const olderEmails = threadEmails.filter(e => e.id !== selectedEmail.id);

              if (olderEmails.length === 0) return null;

              return (
                <div className="border-b border-slate-600">
                  <button
                    onClick={() => toggleThread(threadKey)}
                    className="w-full px-6 py-2 flex items-center gap-2 text-xs font-medium text-slate-400 hover:text-slate-200 hover:bg-slate-900/50 transition-colors"
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-3.5 h-3.5" />
                    ) : (
                      <ChevronRight className="w-3.5 h-3.5" />
                    )}
                    {olderEmails.length} earlier message{olderEmails.length !== 1 ? 's' : ''} in this thread
                  </button>
                  {isExpanded && (
                    <div className="px-6 pb-3 space-y-3">
                      {olderEmails.map((threadEmail) => (
                        <div
                          key={threadEmail.id}
                          className="p-3 bg-slate-900 rounded-lg border border-slate-600/50"
                        >
                          <div className="flex items-center gap-2 mb-1.5">
                            <div className={`w-6 h-6 rounded-full ${getSenderColor(threadEmail.from)} flex items-center justify-center text-[10px] font-bold text-white`}>
                              {getSenderInitials(threadEmail.from)}
                            </div>
                            <span className="text-xs font-medium text-slate-300">
                              {getSenderName(threadEmail.from)}
                            </span>
                            <span className="text-xs text-slate-500">
                              {format(new Date(threadEmail.timestamp), 'MMM d, h:mm a')}
                            </span>
                          </div>
                          <pre className="whitespace-pre-wrap font-sans text-xs text-slate-300 leading-relaxed ml-8">
                            {threadEmail.body}
                          </pre>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Email Body */}
            <div className="flex-1 p-6 overflow-y-auto">
              <div className="prose prose-invert prose-slate max-w-none">
                <pre className="whitespace-pre-wrap font-sans text-slate-200 leading-relaxed">
                  {selectedEmail.body}
                </pre>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Mail className="w-16 h-16 text-slate-600 mb-4 mx-auto" />
              <p className="text-lg text-slate-300">Select an email to read</p>
            </div>
          </div>
        )}
      </div>

      {/* Compose Modal */}
      {composerOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border-2 border-slate-600 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-600">
              <h2 className="text-xl font-bold text-white">Compose Email</h2>
              <button
                onClick={() => setComposerOpen(false)}
                className="p-2 hover:bg-slate-900 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-300" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* To Field */}
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                  To
                </label>
                <ContactAutocomplete
                  contacts={contacts}
                  value={composeData.to}
                  onChange={(value) => setComposeData({ ...composeData, to: value })}
                  placeholder="Type to search contacts..."
                />
              </div>

              {/* Subject Field */}
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  value={composeData.subject}
                  onChange={(e) => setComposeData({ ...composeData, subject: e.target.value })}
                  placeholder="Enter subject"
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
                />
              </div>

              {/* Message Field */}
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                  Message
                </label>
                <textarea
                  value={composeData.body}
                  onChange={(e) => setComposeData({ ...composeData, body: e.target.value })}
                  placeholder="Type your message..."
                  rows={12}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 resize-none font-sans"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-600">
              <button
                onClick={() => setComposerOpen(false)}
                className="px-6 py-2.5 bg-slate-900 hover:bg-slate-700 border border-slate-600 rounded-lg font-medium text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                disabled={sending || !composeData.to || !composeData.body}
                className="flex items-center gap-2 px-6 py-2.5 bg-blue-500 hover:bg-blue-500/90 disabled:bg-blue-500/30 disabled:cursor-not-allowed text-white rounded-lg font-bold transition-colors"
              >
                <Send className="w-4 h-4" />
                {sending ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailInterface;
